<template>
    <div class="cli-callback">
        <template v-if="code">
            <h1>Skarb CLI — authorization code</h1>
            <p>
                Copy the code below and paste it back into the terminal where
                <code class="inline">client.py login</code> is waiting.
            </p>
            <code class="block" ref="codeEl">{{ code }}</code>
            <button @click="copy">Copy</button>
            <span v-if="copied" class="ok">Copied</span>
            <p class="hint">
                The code is single-use and expires in a few minutes. You can
                close this tab after pasting.
            </p>
        </template>
        <template v-else>
            <h1 class="error">Skarb CLI login — failed</h1>
            <p>{{ errorMessage }}</p>
            <p>Close this tab and re-run <code class="inline">client.py login</code>.</p>
        </template>
    </div>
</template>

<script>
// Landing page for the skarb-api CLI copy-paste OAuth flow.
//
// Cognito redirects the browser here after Google SSO. We render the `code`
// from ?code=... so the user can copy it back into the CLI (which runs in a
// sandbox and cannot receive the redirect itself). No token exchange happens
// here — client.py does it directly against Cognito /oauth2/token.
//
// Intentionally does NOT use VHeader or any authenticated API call: the user
// reaches this page before they have a Skarb session.
export default {
    name: 'CliCallback',
    data() {
        return {copied: false};
    },
    computed: {
        code() {
            return this.$route.query.code || '';
        },
        errorMessage() {
            return (
                this.$route.query.error_description ||
                this.$route.query.error ||
                'No code returned.'
            );
        },
    },
    methods: {
        copy() {
            const txt = this.code;
            const done = () => {
                this.copied = true;
            };
            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(txt).then(done, () => {});
                return;
            }
            const el = this.$refs.codeEl;
            if (!el) return;
            const range = document.createRange();
            range.selectNode(el);
            const sel = window.getSelection();
            sel.removeAllRanges();
            sel.addRange(range);
            try {
                document.execCommand('copy');
                done();
            } catch (e) {
                /* noop */
            }
            sel.removeAllRanges();
        },
    },
};
</script>

<style scoped>
.cli-callback {
    font-family: system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
    max-width: 640px;
    margin: 3rem auto;
    padding: 0 1.25rem;
    color: #1a1a1a;
}
.cli-callback h1 {
    font-size: 1.25rem;
    margin: 0 0 0.75rem;
}
.cli-callback h1.error {
    color: #b91c1c;
}
.cli-callback p {
    line-height: 1.55;
    color: #444;
}
.cli-callback code.block {
    display: block;
    background: #f4f4f5;
    border: 1px solid #e4e4e7;
    border-radius: 8px;
    padding: 0.95rem 1rem;
    font-size: 0.95rem;
    word-break: break-all;
    margin: 1.1rem 0;
    user-select: all;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}
.cli-callback code.inline {
    display: inline;
    padding: 0.1em 0.4em;
    background: #f4f4f5;
    border: 1px solid #e4e4e7;
    border-radius: 4px;
}
.cli-callback button {
    appearance: none;
    border: 0;
    border-radius: 8px;
    background: #18181b;
    color: #fff;
    padding: 0.55rem 1.1rem;
    font-size: 0.95rem;
    cursor: pointer;
}
.cli-callback button:hover {
    background: #27272a;
}
.cli-callback button:active {
    transform: translateY(1px);
}
.cli-callback .ok {
    color: #15803d;
    margin-left: 0.6rem;
    font-size: 0.9rem;
}
.cli-callback .hint {
    margin-top: 2rem;
    color: #6b7280;
    font-size: 0.85rem;
}
</style>
